// ═══════════════════════════════════════════════════════════════
// Impact — Infrastructure (Public Assistance) Assessment Form
// Port from InfraAssessment.jsx — Firebase → Ignatius via stores
// ═══════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, TextInput, Textarea, NumberInput, Select, Button,
  Group, Stack, Divider, Alert, LoadingOverlay, Switch,
} from '../../components/gab/GabComponents';
import { IconBackhoe, IconSparkles } from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useAuthStore } from '../../store/authStore';
import { useImpactStore } from '../../store/impactStore';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import type { Assessment } from '../../store/impactStore';
import LocationInput, { type LocationValue } from './components/LocationInput';
import PACategorySelector from './components/PACategorySelector';
import DamageTypeSelector from './components/DamageTypeSelector';
import SeverityRating from './components/SeverityRating';
import PhotoCapture from './components/PhotoCapture';
import MapPicker from './components/MapPicker';
import { primary } from '../../lib/colors';
import { reverseGeocode, getCountyFromCoords } from './lib/gis';
import { WORKER_URL } from '../../lib/api-switch';

// ── Resize helper ──────────────────────────────────────────

const resizeImageToBase64 = (file: File, maxDim = 1200): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      };
      img.onerror = reject;
      img.src = reader.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// ── Facility types ─────────────────────────────────────────

const FACILITY_TYPES = [
  { value: 'government', label: 'Government Building' },
  { value: 'school', label: 'School / Education' },
  { value: 'hospital', label: 'Hospital / Medical' },
  { value: 'fire_station', label: 'Fire Station' },
  { value: 'police_station', label: 'Police Station' },
  { value: 'water_treatment', label: 'Water Treatment Plant' },
  { value: 'power_plant', label: 'Power Plant / Substation' },
  { value: 'bridge', label: 'Bridge' },
  { value: 'road', label: 'Road / Highway' },
  { value: 'dam', label: 'Dam / Levee' },
  { value: 'park', label: 'Park / Recreation' },
  { value: 'transit', label: 'Transit Facility' },
  { value: 'communications', label: 'Communications Tower' },
  { value: 'other', label: 'Other' },
];

// ── Form state ─────────────────────────────────────────────

interface InfraForm {
  paCategory: string;
  severity: number | null;
  facilityName: string;
  facilityType: string;
  damageTypes: string[];
  description: string;
  estimatedRepairCost: string;
  location: LocationValue;
  photos: File[];
}

const INITIAL_FORM: InfraForm = {
  paCategory: '',
  severity: null,
  facilityName: '',
  facilityType: '',
  damageTypes: [],
  description: '',
  estimatedRepairCost: '',
  location: {},
  photos: [],
};

// ── Component ──────────────────────────────────────────────

export default function InfraAssessment() {
  const navigate = useNavigate();
  const { activeIncident, user } = useAuthStore();
  const { createAssessment, uploadPhoto } = useImpactStore();
  const { createRequest } = useDebrisOpsStore();

  const [form, setForm] = useState<InfraForm>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [tagForDebris, setTagForDebris] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  // ── Guard ──────────────────────────────────────────────
  if (!activeIncident) {
    return (
      <Alert color="#C68700" title="No Active Incident">
        <Text>Select an active incident before creating an assessment.</Text>
      </Alert>
    );
  }

  // ── Handlers ───────────────────────────────────────────

  const updateField = <K extends keyof InfraForm>(key: K, value: InfraForm[K]) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const handleLocationChange = (loc: LocationValue) =>
    updateField('location', loc);

  const handleMapClick = (pos: { lat: number; lng: number }) =>
    updateField('location', { ...form.location, lat: pos.lat, lng: pos.lng });

  const handleGpsFromPhoto = (gps: { lat: number; lng: number }) => {
    if (!form.location.lat) {
      updateField('location', { ...form.location, lat: gps.lat, lng: gps.lng });
      notifications.show({ title: 'Photo Location', message: 'GPS coordinates extracted from photo EXIF data.', color: 'var(--color-success)' });
      // Reverse geocode to fill address, county, FIPS
      Promise.all([
        reverseGeocode(gps.lat, gps.lng),
        getCountyFromCoords(gps.lat, gps.lng),
      ]).then(([address, county]) => {
        setForm(prev => ({
          ...prev,
          location: {
            ...prev.location,
            lat: gps.lat,
            lng: gps.lng,
            address: address?.address || prev.location.address || '',
            city: address?.city || prev.location.city || '',
            state: county?.state || address?.state || prev.location.state || '',
            county: county?.county || address?.county || prev.location.county || '',
            fipsCode: county?.fipsCode || prev.location.fipsCode || '',
            zip: address?.zip || prev.location.zip || '',
          },
        }));
      }).catch(() => {});
    }
  };

  // ── AI Photo Analysis ──
  const handleAiDescribe = async () => {
    if (form.photos.length === 0) { notifications.show({ title: 'No Photos', message: 'Add photos first.', color: '#F86A0B' }); return; }
    setAiLoading(true);
    try {
      const images = await Promise.all(
        form.photos.slice(0, 5).map(async (file: File) => {
          const dataUrl = await resizeImageToBase64(file, 1024);
          return { type: 'image' as const, source: { type: 'base64' as const, media_type: 'image/jpeg' as const, data: dataUrl.split(',')[1] } };
        })
      );
      const n = images.length;
      const res = await fetch(`${WORKER_URL}/api/claude`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Origin: 'https://apps.opengov.com' },
        body: JSON.stringify({
          model: 'claude-sonnet-4-6',
          max_tokens: 800,
          messages: [{ role: 'user', content: [...images, { type: 'text', text: `I have uploaded ${n} photo${n > 1 ? 's' : ''} from a public infrastructure damage assessment. Describe the damage visible across ALL ${n} photo${n > 1 ? 's' : ''}, noting what each one shows if they capture different angles or areas. Be concise but thorough (${n > 1 ? '3-5' : '2-3'} sentences). Focus on: type of damage, severity, and structural/functional impact. Do not use emojis.` }] }],
        }),
      });
      const result = await res.json();
      if (result.content?.[0]?.text) {
        updateField('description', result.content[0].text);
        notifications.show({ title: 'AI Description', message: 'Generated from photos.', color: 'var(--color-success)' });
      } else { notifications.show({ title: 'Error', message: 'AI could not generate a description.', color: 'var(--color-error)' }); }
    } catch { notifications.show({ title: 'Error', message: 'Failed to generate AI description.', color: 'var(--color-error)' }); }
    finally { setAiLoading(false); }
  };

  const handleSubmit = async () => {
    // Validation
    if (!form.paCategory) {
      notifications.show({ title: 'Required', message: 'Select a PA category.', color: 'var(--color-error)' });
      return;
    }
    if (!form.location.lat || !form.location.lng) {
      notifications.show({ title: 'Required', message: 'Provide a location (GPS, address, or map).', color: 'var(--color-error)' });
      return;
    }

    setSubmitting(true);
    try {
      // Build flat assessment data
      const assessmentData: Partial<Assessment> = {
        incidentId: String(activeIncident.id),
        assessmentType: 'public',
        paCategory: form.paCategory,
        severity: form.severity,
        address: form.location.address || '',
        county: form.location.county || '',
        fipsCode: form.location.fipsCode || '',
        stateCode: form.location.state || '',
        latitude: form.location.lat || null,
        longitude: form.location.lng || null,
        damageTypes: form.damageTypes,
        description: form.description || '',
        facilityName: form.facilityName || '',
        facilityType: form.facilityType || '',
        repairCost: form.estimatedRepairCost !== '' ? String(form.estimatedRepairCost) : '',
        method: 'form',
        assessorName: user?.fullName || '',
        assessorEmail: user?.email || '',
      };

      const rid = await createAssessment(assessmentData);

      // Upload photos after record is created
      if (form.photos.length > 0) {
        for (let i = 0; i < form.photos.length; i++) {
          try {
            const base64 = await resizeImageToBase64(form.photos[i]);
            await uploadPhoto(rid, base64, i);
          } catch (err) {
            console.error('Photo upload failed:', err);
          }
        }
      }

      // Tag for debris removal if checked
      if (tagForDebris) {
        try {
          await createRequest({
            incidentId: String(activeIncident.id),
            assessmentId: String(rid),
            address: form.location.address || '',
            latitude: form.location.lat ? String(form.location.lat) : '',
            longitude: form.location.lng ? String(form.location.lng) : '',
            damageCategory: form.paCategory || '',
            damageTypes: JSON.stringify(form.damageTypes || []),
            description: form.description || '',
            status: 'pending',
            taggedBy: user?.email ?? '',
            taggedAt: new Date().toISOString(),
          });
        } catch { /* non-blocking */ }
      }

      notifications.show({
        title: 'Assessment Created',
        message: tagForDebris
          ? `Assessment submitted & tagged for debris removal.`
          : `Infrastructure assessment submitted for ${activeIncident.name}.`,
        color: 'var(--color-success)',
      });
      navigate('/impact/assessments');
    } catch (err) {
      console.error('Submit failed:', err);
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to create assessment.',
        color: 'var(--color-error)',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────

  return (
    <div style={{ position: 'relative' }}>
      <LoadingOverlay visible={submitting} />

      <Stack gap="lg">
        <div>
          <Title order={2}>
            <i className="fa-solid fa-building" style={{ marginRight: 10, color: primary[700] }} />
            Infrastructure Assessment
          </Title>
          <Text size="sm" c="dimmed" mt={4}>
            Public Assistance (PA) — Assess damage to public infrastructure and facilities
          </Text>
          <Text size="xs" c="dimmed" mt={2}>
            Incident: <Text span fw={600}>{activeIncident.name}</Text>
          </Text>
        </div>

        <Divider />

        {/* PA Category */}
        <Paper withBorder p="md" radius="md">
          <PACategorySelector
            value={form.paCategory}
            onChange={(val) => updateField('paCategory', val || '')}
          />
        </Paper>

        {/* Facility Info */}
        <Paper withBorder p="md" radius="md">
          <Stack gap="sm">
            <Text fw={600} size="sm">Facility Information</Text>
            <TextInput
              label="Facility Name"
              placeholder="e.g., City Hall, Main St Bridge"
              value={form.facilityName}
              onChange={(e) => updateField('facilityName', e.target.value)}
              size="md"
            />
            <Select
              label="Facility Type"
              placeholder="Select type"
              value={form.facilityType || null}
              onChange={(val) => updateField('facilityType', val || '')}
              data={FACILITY_TYPES}
              searchable
              size="md"
            />
          </Stack>
        </Paper>

        {/* Severity */}
        <Paper withBorder p="md" radius="md">
          <SeverityRating
            value={form.severity}
            onChange={(val) => updateField('severity', val)}
          />
        </Paper>

        {/* Damage Types */}
        <Paper withBorder p="md" radius="md">
          <DamageTypeSelector
            value={form.damageTypes}
            onChange={(val) => updateField('damageTypes', val)}
            mode="pa"
          />
        </Paper>

        {/* Description */}
        <Paper withBorder p="md" radius="md">
          <Stack gap="xs">
            <Textarea
              label="Description"
              placeholder="Describe the damage, scope, and any immediate hazards..."
              value={form.description}
              onChange={(e) => updateField('description', e.target.value)}
              minRows={3}
              autosize
              size="md"
            />
            {form.photos.length > 0 && (
              <Button variant="light" size="xs" leftSection={<IconSparkles size={14} />}
                onClick={handleAiDescribe} loading={aiLoading}>
                AI Describe ({form.photos.length} photo{form.photos.length > 1 ? 's' : ''})
              </Button>
            )}
          </Stack>
        </Paper>

        {/* Estimated Repair Cost */}
        <Paper withBorder p="md" radius="md">
          <NumberInput
            label="Estimated Repair Cost ($)"
            placeholder="e.g., 250000"
            value={form.estimatedRepairCost !== '' ? Number(form.estimatedRepairCost) : ''}
            onChange={(val) => updateField('estimatedRepairCost', val !== '' ? String(val) : '')}
            min={0}
            thousandSeparator=","
            prefix="$"
            size="md"
          />
        </Paper>

        {/* Location */}
        <Paper withBorder p="md" radius="md">
          <LocationInput
            value={form.location}
            onChange={handleLocationChange}
          />
        </Paper>

        {/* Map Picker */}
        <Paper withBorder p="md" radius="md">
          <MapPicker
            value={form.location.lat && form.location.lng ? { lat: form.location.lat, lng: form.location.lng } : null}
            onChange={handleMapClick}
            height={350}
          />
        </Paper>

        {/* Photos */}
        <Paper withBorder p="md" radius="md">
          <Stack gap="sm">
            <Text fw={600} size="sm">Photos</Text>
            <PhotoCapture
              photos={form.photos}
              onChange={(photos) => updateField('photos', photos)}
              onGpsExtracted={handleGpsFromPhoto}
              maxPhotos={10}
            />
          </Stack>
        </Paper>

        <Divider />

        {/* Submit */}
        <Group justify="space-between" align="center">
          <Group gap="md">
            <Button variant="subtle" onClick={() => navigate('/impact/assessments')}>
              Cancel
            </Button>
            <Switch
              label="Tag for Debris Removal"
              checked={tagForDebris}
              onChange={e => setTagForDebris(e.currentTarget.checked)}
              color="#F86A0B"
              thumbIcon={tagForDebris ? <IconBackhoe size={12} /> : undefined}
            />
          </Group>
          <Button
            size="lg"
            loading={submitting}
            onClick={handleSubmit}
            leftSection={<i className="fa-solid fa-paper-plane" />}
          >
            Submit Assessment
          </Button>
        </Group>
      </Stack>
    </div>
  );
}
